import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client';
export { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client';
import type {
  UserProfile, UserUpdateRequest, CreditAccount, LedgerItem, LedgerSummary,
  CursorPage, SubscribeRequest, BoosterPackRequest, OrderResult, OrderListItem,
  DiagnosisSession, SubmitAnswersRequest, DiagnosisResult,
  ContentGeneration, ContentGenerateRequest, RegenerateRequest,
  MediaItem, MediaUploadResult, MediaRequirements, VideoGenerateRequest, EnhanceRequest,
  Plan, DashboardSummary, TrendPoint, Notification, NotificationPreferences,
  PricingInfo, SubscriptionPlan, MerchantProfile, RegisterRequest,
  RegisterResponse, LoginRequest, LoginResponse, CompetitorAnalysisRequest,
  MixcutRequest, MixcutCreateResult, DigitalHumanRequest, DigitalHumanCreateResult,
  VoiceCloneRequest, VoiceCloneResult, VideoProject, VideoProjectList, BgmLibrary,
  AiImageResult,
  DeviceLoginResponse,
} from '@/types';

export const authApi = {
  register: (req: RegisterRequest) => apiPost<RegisterResponse>('/auth/register', req),
  login: (req: LoginRequest) => apiPost<LoginResponse>('/auth/login', req),
  refresh: (refreshToken: string) => apiPost<{ access_token: string }>('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => apiPost<void>('/auth/logout'),
  /** 发送短信验证码（登录/绑定手机号共用） */
  sendCode: (phone: string) => apiPost<void>('/auth/send-code', { phone }),
  /** 设备免验证登录（本机一键登录） */
  deviceLogin: (device_id: string, device_info: string) =>
    apiPost<DeviceLoginResponse>('/auth/device-login', { device_id, device_info }),
  /** 登录后绑定手机号（需短信验证码确认） */
  bindPhone: (phone: string, verification_code: string) =>
    apiPost<{ phone_bound: boolean; phone: string }>('/auth/bind-phone', { phone, verification_code }),
  /** 设置登录账号（手机号 + 密码，无需短信，用于跨设备/换容器自助登录同步）
   *  phone 传 null：账号已绑定手机号、仅设置/修改密码（后端用库中真实号，前端只有脱敏号） */
  setLogin: (phone: string | null, password: string) =>
    apiPost<{ phone: string }>('/auth/set-login', phone ? { phone, password } : { password }),
  /** 更换已绑定手机号（短信不可用，用当前登录密码验证身份） */
  changePhone: (new_phone: string, password: string) =>
    apiPost<{ phone: string }>('/auth/change-phone', { new_phone, password }),
};

export const userApi = {
  getMe: () => apiGet<UserProfile>('/users/me'),
  updateMe: (req: UserUpdateRequest) => apiPatch<UserProfile>('/users/me', req),
  deleteAccount: () => apiDelete<void>('/users/me', { confirmation: 'CONFIRM_DELETE' }),
};

export const creditApi = {
  getAccount: () => apiGet<CreditAccount>('/credits/account'),
  getLedger: (params: { page: 'consume' | 'acquire'; type?: string; cursor?: string; limit?: number }) =>
    apiGet<CursorPage<LedgerItem>>('/credits/ledger', params as Record<string, unknown>),
  getLedgerSummary: () => apiGet<LedgerSummary>('/credits/ledger/summary'),
  exportLedger: (params: { start_date: string; end_date: string; format: 'csv' | 'xlsx' }) =>
    apiGet<{ download_url: string }>('/credits/ledger/export', params as Record<string, unknown>),
  getPricing: () => apiGet<PricingInfo[]>('/credits/pricing'),
};

export const subscriptionApi = {
  getPlans: () => apiGet<SubscriptionPlan[]>('/subscriptions/plans'),
  subscribe: (req: SubscribeRequest) => apiPost<OrderResult>('/subscriptions/subscribe', req),
};

export const boosterPackApi = {
  purchase: (req: BoosterPackRequest) => apiPost<OrderResult>('/credits/purchase', req),
};

export const orderApi = {
  getOrder: (id: string) => apiGet<OrderResult>(`/orders/${id}`),
  /** 我的订单列表（最新在前，可按状态筛选） */
  list: (params?: { status?: string; limit?: number; offset?: number }) =>
    apiGet<{ items: OrderListItem[]; total: number; limit: number; offset: number }>(
      '/orders', params as Record<string, unknown>,
    ),
  /** 取消未完成订单（仅 pending/paying 可取消） */
  cancel: (id: string) => apiPost<OrderListItem>(`/orders/${id}/cancel`),
  /** 继续支付未完成订单（复用原订单，返回新支付链接） */
  pay: (id: string, returnUrl?: string) =>
    apiPost<OrderResult>(`/orders/${id}/pay`, returnUrl ? { return_url: returnUrl } : {}),
};

export const diagnosisApi = {
  createSession: (modules: string[], platform?: string, industry?: string) =>
    apiPost<DiagnosisSession>('/diagnosis/sessions', {
      modules_selected: modules,
      platform,
      industry,
    }),
  getSession: (id: string) => apiGet<DiagnosisSession>(`/diagnosis/sessions/${id}`),
  submitAnswers: (sessionId: string, moduleCode: string, answers: SubmitAnswersRequest) =>
    apiPost<void>(`/diagnosis/sessions/${sessionId}/modules/${moduleCode}/answers`, answers),
  getResult: (sessionId: string) => apiGet<DiagnosisResult>(`/diagnosis/sessions/${sessionId}/result`),
  getHistory: (cursor?: string, limit = 10) =>
    apiGet<CursorPage<DiagnosisSession>>('/diagnosis/history', { cursor, limit } as Record<string, unknown>),
  compare: (sessionId: string, compareId: string) =>
    apiPost<DiagnosisResult>(`/diagnosis/sessions/${sessionId}/compare`, { compare_session_id: compareId }),
  competitorAnalysis: (req: CompetitorAnalysisRequest) =>
    apiPost<DiagnosisResult>('/diagnosis/competitor-analysis', req),
  exportPdf: (sessionId: string) =>
    apiPost<{ download_url: string }>(`/diagnosis/sessions/${sessionId}/export-pdf`),
  // v4.0 按量计费 — 新增 API
  estimateCost: (params: { modules: string; screenshots_per_module?: number; questions_per_module?: number; guidance_items?: number; guidance_tier?: string }) =>
    apiGet<{ total_credits: number; estimated_yuan: number; breakdown: { tier: string; count: number; unit_cost: number; subtotal: number }[]; pricing_reference: Record<string, { name: string; cost: number; desc: string }> }>(
      '/diagnosis/cost/estimate', params as Record<string, unknown>,
    ),
  getBalance: () =>
    apiGet<{ balance: number; estimated_yuan: number }>('/diagnosis/balance'),
};

// v4.0 截图诊断 & 指导方案 API
export const screenshotApi = {
  analyze: (params: { session_id: string; module_code: string; platform?: string; item_code?: string; file: File }) => {
    const formData = new FormData();
    formData.append('session_id', params.session_id);
    formData.append('module_code', params.module_code);
    if (params.platform) formData.append('platform', params.platform);
    if (params.item_code) formData.append('item_code', params.item_code);
    formData.append('file', params.file);
    return apiPost<{
      attachment_id: string; module_code: string; platform: string;
      credits_deducted: number; remaining_balance: number;
      extracted_data: Record<string, unknown>; ocr_text_preview: string;
      screenshot_count: number; screenshot_limit: number;
    }>('/diagnosis/screenshots/analyze', formData);
  },
  visionAnalyze: (params: { files: File[]; module_code?: string; platform?: string }) => {
    const formData = new FormData();
    params.files.forEach((f) => formData.append('files', f));
    if (params.module_code) formData.append('module_code', params.module_code);
    if (params.platform) formData.append('platform', params.platform);
    return apiPost<{
      results: Array<{ filename: string; ocr_text: string; extracted_data: Record<string, unknown>; file_size: number }>;
      total: number; module_code: string; platform: string;
    }>('/diagnosis/screenshots/vision-analyze', formData);
  },
  getGuide: (platform?: string, moduleCode?: string) =>
    apiGet<{
      platform?: string; module_code?: string; source?: string; tip?: string;
      required_screenshots?: string[]; max_screenshots?: number; platforms?: Record<string, string[]>;
      guide_by_dimension?: Array<{
        code: string; name: string; weight: number; priority: string;
        fields: Record<string, string[]>;
      }>;
      upload_groups?: Record<string, string[]>;
      guide_by_page?: Array<{
        page: string; path: string; priority: string; shots: number; tip: string;
        dims: string[]; fields: Record<string, string[]>; field_count: number;
      }>;
      page_summary?: {
        page_count: number; total_shots: number; covered_fields: number; total_fields: number;
      };
    }>(
      '/diagnosis/screenshots/guide', { platform, module_code: moduleCode } as Record<string, unknown>,
    ),
  generateGuidance: (params: { session_id: string; result_id: string; item_code: string; tier?: string }) =>
    apiPost<{
      suggestion_id: string; item_code: string; item_name: string; score: number;
      guidance_tier: string; content: string; credits_deducted: number;
      remaining_balance: number; model_used: string;
    }>('/diagnosis/guidance/generate', params),
  batchGuidance: (params: { session_id: string; result_id: string; item_codes: string; tier?: string }) =>
    apiPost<{
      total_items: number; discount: number; unit_cost: number;
      total_credits: number; results: Record<string, unknown>[]; remaining_balance: number;
    }>('/diagnosis/guidance/batch', params),
};

// 工具模式诊断 API (AM/SV/CM/LD/PS/PD/GEO)
// 截图自动发现结果：needs_confirmation=true 时前端弹窗让商家从 candidates 选平台
export interface RuleDiscoveryResult {
  needs_confirmation?: boolean;
  candidates?: string[];
  platform?: string;
  proposals?: string[];
  ranking?: Array<[string, number]>;
}

export const toolDiagnosisApi = {
  analyze: (params: { files: File[]; module_code: string; platform?: string; confirmed_platform?: string; session_id?: string }) => {
    const formData = new FormData();
    params.files.forEach((f) => formData.append('files', f));
    formData.append('module_code', params.module_code);
    if (params.platform) formData.append('platform', params.platform);
    if (params.confirmed_platform) formData.append('confirmed_platform', params.confirmed_platform);
    if (params.session_id) formData.append('session_id', params.session_id);
    return apiPost<{
      detected_platform: string;
      confidence: number;
      module_code: string;
      extracted_data: Record<string, unknown>;
      extracted_by_dimension?: Array<{
        code: string; name: string; weight: number; priority: string;
        matched: Array<{ key: string; value: unknown; item_code: string; item_name: string; priority: string }>;
        matched_count: number; item_total: number;
      }>;
      unmatched_keys?: string[];
      dimension_coverage: Record<string, { name: string; covered: boolean; missing_items?: string[]; priority?: string; matched_count?: number }>;
      completeness: number;
      missing_hints: string[];
      screenshot_count: number;
      results: Array<{ filename: string; ocr_text?: string; extracted_data?: Record<string, unknown>; error?: string }>;
      rule_discovery?: RuleDiscoveryResult;
      attachment_id?: string;
      attachment_ids?: string[];
    }>('/diagnosis/tool/analyze', formData);
  },
  // 截图自动发现—商家确认平台后补跑规则发现（免费，不重跑 OCR、不重复扣积分）
  discoverConfirm: (params: {
    module_code: string;
    confirmed_platform: string;
    declared_platform?: string;
    ocr_text?: string;
    extracted_data: Record<string, unknown>;
    session_id?: string;
  }) =>
    apiPost<{ rule_discovery: RuleDiscoveryResult }>('/diagnosis/tool/discover-confirm', params),
  // GEO 排名检索 — 检查品牌在五大AI平台的排名表现
  geoRankCheck: (params: {
    brand_name: string;
    industry: string;
    keywords: string[];
    location?: string;
    website?: string;
    competitors?: string[];
  }) =>
    apiPost<{
      brand_name: string;
      overall_geo_score: number;
      platform_rankings: Array<{
        platform: string;
        provider: string;
        ranking_score: number;
        visibility_level: string;
        citation_probability: number;
        citation_position: string;
        citation_quality: string;
        strengths: string[];
        weaknesses: string[];
        improvement_tips: string[];
      }>;
      competitor_comparison: {
        brand_visibility: string;
        competitor_visibility: string;
        gap_analysis: string;
      };
      extracted_data: Record<string, unknown>;
      top_issues: Array<{ issue: string; severity: string; platform: string }>;
      optimization_priorities: Array<{ priority: number; action: string; expected_effect: string; difficulty: string }>;
    }>('/diagnosis/tool/geo-rank-check', params),
  score: (params: { module_code: string; platform: string; extracted_data: Record<string, unknown>; session_id?: string }) =>
    apiPost<{
      module_code: string;
      platform: string;
      composite_score: number;
      grade: string;
      dimensions: Array<{
        code: string;
        name: string;
        score: number;
        weight: number;
        priority?: string;
        collected?: boolean;
        items: Array<{
          code: string;
          name: string;
          score: number;
          status: string;
          priority?: string;
          weight?: number;
          current_value: string;
          threshold_pass: string;
          problem: string;
          consequence: string;
          solution: string;
        }>;
      }>;
      rules_used: string;
      result_id: string | null;
      session_id?: string | null;
      item_codes: string[];
    }>('/diagnosis/tool/score', params),
  report: (params: { module_code: string; platform: string; score_result: Record<string, unknown> }) =>
    apiPost<{
      module_code: string;
      platform: string;
      composite_score: number;
      grade: string;
      report: {
        summary: string;
        score_composition?: Array<{
          dimension: string;
          contribution: string;
          lost_points: string;
          improve_potential: string;
        }>;
        causal_analysis?: string[];
        dimensions: Array<{
          name: string;
          score: number;
          data_evidence?: string;
          problems: Array<{
            item: string;
            problem: string;
            consequence: string;
            solution: string;
            priority: string;
          }>;
        }>;
        improvement_plan?: Array<{
          title: string;
          evidence: string;
          root_cause: string;
          steps: string[];
          benefit: string;
          timeline: string;
        }>;
        action_plan: Array<{
          priority: number;
          action: string;
          expected_effect: string;
          timeline: string;
        }>;
        strengths: string[];
      };
      credits_consumed: number;
      credits_frozen?: number;
      suggestion_ids?: string[];
    }>('/diagnosis/tool/report', params),
};

/**
 * 后端返回格式 → 前端 ContentGeneration 格式映射
 * 后端: { content: { text: "...", metadata: {...} }, credits_consumed: N }
 * 前端: { result: { content: "...", metadata: {...} }, credit_consumed: N }
 */
function _normalizeGeneration(raw: Record<string, unknown>): ContentGeneration {
  const contentObj = raw.content as { text?: string; metadata?: Record<string, unknown> } | undefined;
  return {
    generation_id: (raw.generation_id as string) ?? '',
    operation_code: (raw.operation_code as string) ?? '',
    status: (raw.status as ContentGeneration['status']) ?? 'pending',
    result: contentObj
      ? { content: contentObj.text ?? '', metadata: contentObj.metadata ?? {} }
      : undefined,
    media_urls: (raw.media_urls as string[] | undefined) ?? [],
    credit_consumed: (raw.credits_consumed as number) ?? 0,
    created_at: (raw.created_at as string) ?? '',
  };
}

export const contentApi = {
  generate: async (req: ContentGenerateRequest) => {
    const raw = await apiPost<Record<string, unknown>>('/content/generate', req);
    return _normalizeGeneration(raw);
  },
  getGeneration: async (id: string) => {
    const raw = await apiGet<Record<string, unknown>>(`/content/generations/${id}`);
    return _normalizeGeneration(raw);
  },
  getGenerations: async (params?: { operation_code?: string; status?: string; cursor?: string; limit?: number }) => {
    const raw = await apiGet<{ items: Record<string, unknown>[]; has_more: boolean; next_cursor: string | null }>('/content/generations', params as Record<string, unknown>);
    return {
      items: (raw.items ?? []).map(_normalizeGeneration),
      has_more: raw.has_more ?? false,
      next_cursor: raw.next_cursor,
    };
  },
  regenerate: async (id: string, req: RegenerateRequest) => {
    const raw = await apiPost<Record<string, unknown>>(`/content/generations/${id}/regenerate`, req);
    return _normalizeGeneration(raw);
  },
  generateVideo: async (req: VideoGenerateRequest) => {
    const raw = await apiPost<Record<string, unknown>>('/content/generate/video', req);
    return _normalizeGeneration(raw);
  },
  enhance: async (req: EnhanceRequest) => {
    const raw = await apiPost<Record<string, unknown>>('/content/generate/enhance', req);
    return _normalizeGeneration(raw);
  },
  /** 保存/更新生成内容 (商家编辑后保存到历史) */
  saveContent: async (id: string, content: string, approved?: boolean) => {
    const raw = await apiPut<Record<string, unknown>>(`/content/generations/${id}`, { content, approved });
    return _normalizeGeneration(raw);
  },
  /** AI宣传图: 朋友圈文案+商家素材图 → 深度推理 → 推广图+配套文案 (C-025) */
  generateAiImage: (req: {
    moments_copy: string;
    materials: { url: string; kind: string }[];
    image_count: number;
    requirements?: string;
  }) => apiPost<AiImageResult>('/content/ai-image/generate', req),
};

export const mediaApi = {
  upload: (files: File[], category?: string, contentTypeHint?: string) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    if (category) form.append('category', category);
    if (contentTypeHint) form.append('content_type_hint', contentTypeHint);
    return apiPost<MediaUploadResult>('/content/media/upload', form);
  },
  list: (params?: { category?: string; file_type?: string; limit?: number; offset?: number }) =>
    apiGet<{ items: MediaItem[]; total: number }>('/content/media', params as Record<string, unknown>),
  remove: (id: string) => apiDelete<{ deleted: boolean }>(`/content/media/${id}`),
  requirements: (industry: string, contentType: string) =>
    apiGet<MediaRequirements>('/content/media/requirements', { industry, content_type: contentType }),
};

/** 视频能力 — AI智能混剪 / 数字人视频 / 声音复刻 / 项目查询 / BGM曲库 */
export const videoApi = {
  /** 创建 AI 智能混剪项目 (C-024), 返回 project_id, 渲染异步进行 */
  createMixcut: (req: MixcutRequest) =>
    apiPost<MixcutCreateResult>('/content/video/mixcut', req),
  /** 创建数字人视频项目 (V-041 照片驱动 / V-042 口播口型替换) */
  createDigitalHuman: (req: DigitalHumanRequest) =>
    apiPost<DigitalHumanCreateResult>('/content/video/digital-human', req),
  /** 声音复刻·音色定制 (V-043), 同步返回 voice_id */
  cloneVoice: (req: VoiceCloneRequest) =>
    apiPost<VoiceCloneResult>('/content/video/voice-clone', req),
  /** 查询单个项目进度/成片 (前端轮询) */
  getProject: (id: string) =>
    apiGet<VideoProject>(`/content/video/projects/${id}`),
  /** 项目列表 (分页, 可按类型/状态筛选) */
  listProjects: (params?: { project_type?: string; status?: string; limit?: number; offset?: number }) =>
    apiGet<VideoProjectList>('/content/video/projects', params as Record<string, unknown>),
  /** 免版权 BGM 曲库 */
  bgmLibrary: (params?: { style?: string; industry?: string }) =>
    apiGet<BgmLibrary>('/content/video/bgm-library', params as Record<string, unknown>),
};

export const planApi = {
  create: (req: { plan_type: string; plan_name: string; plan_config?: Record<string, unknown>; start_date?: string; end_date?: string; diagnosis_session_id?: string }) =>
    apiPost<Plan>('/plans', req),
  getList: () => apiGet<Plan[]>('/plans'),
  getDetail: (id: string) => apiGet<Plan>(`/plans/${id}`),
};

export const dashboardApi = {
  getSummary: () => apiGet<DashboardSummary>('/dashboard/summary'),
  getRadar: () => apiGet<{ modules: { module_code: string; score: number; module_name: string }[] }>('/dashboard/radar'),
  getTrends: (params?: { metric_type?: string; period?: string }) =>
    apiGet<TrendPoint[]>('/dashboard/trends', params as Record<string, unknown>),
  getFollowUp: () => apiGet<{
    needs_follow_up: boolean;
    days_since_last: number | null;
    last_diagnosis_at: string | null;
    score_comparison: {
      current_score: number;
      current_session_id: string;
      previous_score: number | null;
      previous_session_id: string | null;
      change: number;
      trend: 'up' | 'down' | 'flat';
      module_changes: { module_code: string; current_score: number; previous_score: number; change: number }[];
    } | null;
    message: string;
  }>('/dashboard/follow-up'),
};

export const notificationApi = {
  getList: () => apiGet<Notification[]>('/notifications'),
  markRead: (ids: string[]) => apiPost<void>('/notifications/read', { notification_ids: ids }),
  updatePreferences: (prefs: NotificationPreferences) =>
    apiPost<void>('/notifications/preferences', prefs),
};

export const profileApi = {
  create: (data: {
    basic_info: Record<string, unknown>;
    account_info?: Record<string, unknown>;
  }) =>
    apiPost<MerchantProfile>('/profiles', data),
  getMyProfile: () => apiGet<Record<string, unknown>>('/profiles/me'),
  uploadOcr: (profileId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<{ ocr_text: string }>(`/profiles/${profileId}/ocr`, formData);
  },
};

// v4.0 客户画像 API
export const customerProfileApi = {
  get: () => apiGet<{
    exists: boolean;
    profile: {
      target_age_range: string;
      target_gender_distribution: { male: number; female: number } | null;
      target_location: { type: string; radius: string } | null;
      target_interests: string[] | null;
      target_consumption_level: string;
      target_pain_points: string;
      target_buying_frequency: string;
      core_customer_segment: string;
      customer_layers: { core: string; potential: string; low_efficiency: string } | null;
      repurchase_rate: number | null;
      avg_customer_value: string | null;
      top_consultation_questions: string[] | null;
      customer_lifecycle_sop: Record<string, string> | null;
      data_source: string;
      completeness_score: number;
      version: number;
      created_at: string | null;
      updated_at: string | null;
    } | null;
  }>('/customer-profile'),
  generate: (force = false) =>
    apiPost<{
      generated: boolean;
      credits_consumed: number;
      completeness_score: number;
      profile: Record<string, unknown>;
    }>('/customer-profile/generate', { force }),
  getStatus: () => apiGet<{
    profile_exists: boolean;
    basic_info_completed: boolean;
    business_ops_completed: boolean;
    cp_completed: boolean;
    can_generate: boolean;
    cp_exists: boolean;
    completeness_score: number;
    data_source: string | null;
    version: number;
    reason?: string;
  }>('/customer-profile/status'),
  remove: () => apiDelete<{ deleted: boolean }>('/customer-profile'),
  update: (data: {
    target_age_range?: string;
    target_gender_distribution?: { male: number; female: number };
    target_location?: { type: string; radius: string };
    target_interests?: string[];
    target_consumption_level?: string;
    target_pain_points?: string;
    target_buying_frequency?: string;
    core_customer_segment?: string;
    customer_layers?: { core: string; potential: string; low_efficiency: string };
    repurchase_rate?: number;
    avg_customer_value?: string;
    top_consultation_questions?: string;
    customer_lifecycle_sop?: { acquisition: string; conversion: string; retention: string };
  }) => apiPatch<{
    updated: boolean;
    completeness_score: number;
    profile: { core_customer_segment: string; target_age_range: string; target_consumption_level: string };
  }>('/customer-profile', data),
  confirm: (corrections?: Record<string, unknown>) =>
    apiPost<{
      confirmed: boolean;
      pp_generated: boolean;
      profile: { core_customer_segment: string; target_age_range: string; target_consumption_level: string; quality_score: number | null };
    }>('/customer-profile/confirm', { corrections }),
};

export const ipPersonaApi = {
  get: () => apiGet<{
    exists: boolean;
    persona: {
      persona_type: string;
      persona_positioning: string;
      professional_background: string;
      personal_traits: string;
      expertise_areas: string[] | null;
      memorable_elements: string;
      trust_building_methods: string[] | null;
      differentiation: string;
      willing_to_appear: boolean;
      appearance_style: string;
      tone_of_voice: string;
      visual_style: string;
      content_direction: string[] | null;
      role_models: string;
      completeness_score: number;
      version: number;
      created_at: string | null;
      updated_at: string | null;
    } | null;
  }>('/ip-persona'),
  update: (data: {
    appearance: string;
    style: string[];
    expertise: string[];
    differentiation: string[];
    persona_positioning?: string;
    personal_traits?: string[];
    memorable_elements?: string;
    trust_building?: string[];
    visual_style?: string;
    content_direction?: string[];
    role_models?: string;
  }) => apiPatch<{
    updated: boolean;
    persona_type: string;
    appearance_style: string;
  }>('/ip-persona', data),
  confirm: (corrections?: Record<string, unknown>) =>
    apiPost<{
      confirmed: boolean;
      persona_type: string;
      quality_score: number | null;
    }>('/ip-persona/confirm', { corrections }),
  generate: (force = false) =>
    apiPost<{
      generated: boolean;
      credits_consumed: number;
      completeness_score: number;
      profile: Record<string, unknown>;
    }>('/ip-persona/generate', { force }),
};

export const onboardingApi = {
  complete: () => apiPatch<UserProfile>('/users/me', { is_onboarded: true }),
  getStatus: () => apiGet<{
    profile_exists: boolean;
    basic_info_completed: boolean;
    business_ops_completed: boolean;
    pp_completed: boolean;
    cp_completed: boolean;
    onboarding_step: string;
    is_onboarding_complete: boolean;
    missing_steps: string[];
  }>('/profiles/onboarding-status'),
  generateReport: () => apiPost<{
    report_id: string;
    generated_at: string;
    completeness_score: number;
    merchant: Record<string, unknown>;
    customer_profile: {
      core_customer_segment: string;
      target_age_range: string;
      target_gender_distribution: { male: number; female: number } | null;
      target_consumption_level: string;
      target_pain_points: string;
      target_buying_frequency: string;
      target_location: { type: string; radius: string } | null;
      target_interests: string[] | null;
      customer_layers: { core: string; potential: string; low_efficiency: string } | null;
      repurchase_rate: number | null;
      data_source: string;
      quality_score: number | null;
      analysis_reasoning: string | null;
      cross_validation_result: { passed: boolean; checks: Array<{ field: string; issue: string; suggestion: string }> } | null;
    } | null;
    ip_persona: {
      persona_type: string;
      positioning: string;
      professional_background: string;
      personal_traits: string;
      willing_to_appear: boolean | null;
      appearance_style: string;
      tone_of_voice: string;
      visual_style: string;
      differentiation: string;
      memorable_elements: string;
      expertise_areas: string[] | null;
      content_direction: string[] | null;
      quality_score: number | null;
      strategy_reasoning: string | null;
      cross_validation_result: { passed: boolean; checks: Array<{ field: string; issue: string; suggestion: string }> } | null;
    } | null;
  }>('/profiles/report'),
  optimize: () => apiPost<{
    report_id: string;
    generated_at: string;
    completeness_score: number;
    optimized: boolean;
    optimization_summary: string;
    merchant: Record<string, unknown>;
    customer_profile: {
      core_customer_segment: string;
      target_age_range: string;
      target_gender_distribution: { male: number; female: number } | null;
      target_consumption_level: string;
      target_pain_points: string;
      target_buying_frequency: string;
      target_location: { type: string; radius: string } | null;
      target_interests: string[] | null;
      customer_layers: { core: string; potential: string; low_efficiency: string } | null;
      repurchase_rate: number | null;
      data_source: string;
      quality_score: number | null;
      analysis_reasoning: string | null;
      cross_validation_result: { passed: boolean; checks: Array<{ field: string; issue: string; suggestion: string }> } | null;
    } | null;
    ip_persona: {
      persona_type: string;
      positioning: string;
      professional_background: string;
      personal_traits: string;
      willing_to_appear: boolean | null;
      appearance_style: string;
      tone_of_voice: string;
      visual_style: string;
      differentiation: string;
      memorable_elements: string;
      expertise_areas: string[] | null;
      content_direction: string[] | null;
      quality_score: number | null;
      strategy_reasoning: string | null;
      cross_validation_result: { passed: boolean; checks: Array<{ field: string; issue: string; suggestion: string }> } | null;
    } | null;
  }>('/profiles/optimize', undefined, 180000),  // 180s timeout for AI optimization
};

// ---------------------------------------------------------------------------
// Operations Assessment API (post-onboarding assessment & routing)
// ---------------------------------------------------------------------------

export interface DimensionStatus {
  status: 'doing' | 'not_doing' | 'unknown';
  note: string;
}

export interface ModuleAssessmentData {
  status: 'in_progress' | 'not_started' | 'not_applicable' | 'unknown';
  good_points: string;
  weak_points: string;
  dimensions: Record<string, DimensionStatus>;
}

export interface PlanFrameworkModule {
  code: string;
  name: string;
  weight: number;
  is_applicable: boolean;
  is_completed_in_onboarding: boolean;
  dimensions: Array<{
    code: string;
    name: string;
    weight: number;
    items: Array<{ code: string; name: string }>;
  }>;
  description: string;
}

export interface PlanFramework {
  industry: string;
  platforms: string[];
  modules: Record<string, PlanFrameworkModule>;
  generated_at: string;
}

// ---------------------------------------------------------------------------
// Operations Plan types (全方位运营方案)
// ---------------------------------------------------------------------------

export interface PlatformRecommendation {
  code: string;
  name: string;
  icon: string;
  priority: 'core' | 'recommended';
  already_has: boolean;
  best_for: string;
}

export interface AccountSetupItem {
  platform_code: string;
  platform_name: string;
  icon: string;
  is_existing: boolean;
  setup_steps: string[];
  optimization_tips: string[];
  action: string;
}

export interface ContentStrategy {
  themes: string[];
  posting_frequency: string;
  content_ratio: Record<string, string>;
  tips: string[];
}

export interface ShortVideoPlan {
  enabled: boolean;
  recommendation?: string;
  account_strategy?: {
    account_type: string;
    positioning: string;
    matrix_hint: string;
  };
  content_pillars?: Array<{ name: string; desc: string; ratio: string }>;
  topics?: string[];
  weekly_plan?: Array<{ day: string; type: string; topic?: string; desc?: string; hook?: string; hashtag?: string }>;
  production_tips?: string[];
  production_guidelines?: {
    equipment: string[];
    shooting_tips: string[];
    editing_tips: string[];
    posting_rules: string[];
  };
  general_tips?: string[];
}

export interface LiveStreamPlan {
  enabled: boolean;
  recommendation?: string;
  formats?: Array<{ name: string; desc: string; script: string; duration: string; frequency: string }>;
  script_template?: string;
  team_roles?: string;
  equipment_list?: string[];
  conversion_tactics?: string[];
  frequency?: string;
  best_times?: string;
  kpi_hint?: string;
  duration?: string;
  themes?: string[];
  tips?: string;
  preparation_checklist?: string[];
}

export interface MomentsPlan {
  daily_schedule: Array<{ time: string; type: string; desc: string }>;
  content_rules: string[];
  customer_management: string[];
}

export interface ContentCalendarWeek {
  week: number;
  focus: string;
  content_theme: string;
  tasks: string[];
}

export interface ActionChecklistItem {
  priority: string;
  task: string;
  deadline: string;
}

export interface PlatformAccountDetail {
  platform: string;
  account_name: string;
  followers: string;
  status: string;
  highlights: string;
}

export interface OperationGuidance {
  daily_routine: string[];
  weekly_plan: string[];
  short_video_guidance: {
    frequency: string;
    best_times: string;
    key_metrics: string[];
    content_mix: string;
  };
  live_stream_guidance: {
    frequency: string;
    duration: string;
    best_times: string;
    key_metrics: string[];
    preparation: string;
  };
  moments_guidance: {
    daily_count: string;
    best_times: string[];
    content_ratio: string;
  };
}

export interface CurrentStatusAssessment {
  stage: string;
  score_hint: number;
  platform_accounts_detail: PlatformAccountDetail[];
  strengths: string[];
  weaknesses: string[];
  immediate_focus: string;
  operation_guidance: OperationGuidance;
}

export interface DailyMustDoItem {
  category: string;
  task: string;
}

export interface WeeklyTaskItem {
  week: number;
  tasks: string[];
}

export interface ActionChecklist {
  daily_must_do: DailyMustDoItem[];
  weekly_tasks: WeeklyTaskItem[];
  monthly_goals: string[];
  team_hint: string;
}

export interface OperationsPlan {
  overview: {
    business_name: string;
    industry: string;
    sub_industry: string;
    city: string;
    team_size: number;
    has_content_staff: boolean;
    content_capacity: string;
    existing_platforms: string[];
    total_recommended_platforms: number;
  };
  current_status_assessment: CurrentStatusAssessment;
  platform_recommendations: PlatformRecommendation[];
  account_setup: AccountSetupItem[];
  content_strategy: ContentStrategy;
  short_video_plan: ShortVideoPlan;
  live_stream_plan: LiveStreamPlan;
  moments_plan: MomentsPlan;
  content_calendar: ContentCalendarWeek[];
  action_checklist: ActionChecklist | ActionChecklistItem[];
  kpi_targets: Record<string, string>;
}

export interface AssessmentResult {
  diagnosis_modules: string[];
  plan_modules: string[];
  skip_modules: string[];
  estimated_diagnosis_cost: number;
  estimated_plan_cost: number;
}

export const opsAssessmentApi = {
  // 异步派发: 端点只 freeze+落库(generating)+派发后台任务并立即返回, 结果由轮询 getStatus 获取
  generate: () => apiPost<{
    status: 'generating';
    message?: string;
  }>('/ops-assessment/generate', {}, 30000),  // 30s — 仅派发耗时, 不再等待 AI 生成

  submit: (data: Record<string, Omit<ModuleAssessmentData, 'dimensions'> & {
    dimensions?: Record<string, DimensionStatus>;
  }>) => apiPost<AssessmentResult>('/ops-assessment/submit', { assessment_data: data }),

  getStatus: () => apiGet<{
    has_framework: boolean;
    status: 'not_started' | 'generating' | 'draft' | 'submitted' | 'diagnosed' | 'planned' | 'failed';
    ai_summary: string;
    plan_framework: PlanFramework | null;
    operations_plan: OperationsPlan | null;
    assessment_data: Record<string, ModuleAssessmentData> | null;
    diagnosis_modules: string[];
    plan_modules: string[];
    skip_modules: string[];
    submitted_at: string | null;
  }>('/ops-assessment/status'),
};

// ---------------------------------------------------------------------------
// Daily Tasks API (每日任务)
// ---------------------------------------------------------------------------

export interface TodayTask {
  id: string;
  task_source: 'plan' | 'custom' | 'ai_daily';
  source_ref: string | null;
  title: string;
  description: string | null;
  platform: string | null;
  task_type: string | null;
  priority: string | null;
  deadline: string | null;
  is_completed: boolean;
  is_recurring?: boolean;
  created_at?: string | null;
  category?: string;
  time_hint?: string;
}

export interface AvailableTask {
  source_ref: string;
  title: string;
  priority: string;
  deadline: string;
  task_type: string;
}

export interface DailyTask {
  id: string;
  title: string;
  task_source: string;
}

export const dailyTaskApi = {
  getToday: () => apiGet<TodayTask[]>('/tasks/today'),
  getAvailable: () => apiGet<AvailableTask[]>('/tasks/available'),
  complete: (data: { task_id?: string; source?: string; source_ref?: string }) =>
    apiPost<{ completed: boolean; already_completed?: boolean }>('/tasks/complete', data),
  addCustom: (data: { title: string; description?: string; platform?: string; task_type?: string; source_ref?: string }) =>
    apiPost<DailyTask>('/tasks/custom', data),
  deleteCustom: (taskId: string) => apiDelete<void>(`/tasks/custom/${taskId}`),
};

// ---------------------------------------------------------------------------
// Dashboard Review Items API (诊断复盘)
// ---------------------------------------------------------------------------

export interface ReviewItem {
  platform: string;
  platform_name: string;
  icon: string;
  review_type: string;
  review_type_name: string;
  description: string;
}

export const dashboardReviewApi = {
  getReviewItems: () => apiGet<ReviewItem[]>('/dashboard/review-items'),
};

// ---------------------------------------------------------------------------
// Agent promotion & merchant binding API (P1 推广闭环)
// ---------------------------------------------------------------------------

export interface PromoAgentInfo {
  valid: boolean;
  promo_code?: string;
  agent_name?: string;
  agent_level?: string;
  level_name?: string;
}

export interface MyBinding {
  bound: boolean;
  agent_name?: string;
  agent_code?: string;
  promo_code?: string;
  binding_source?: string;
  binding_time?: string | null;
}

export interface ClaimItem {
  id: string;
  agent_name: string;
  message: string;
  created_at: string | null;
}

export const promoApi = {
  /** 公开：落地页展示推荐人信息（无效码返回 valid=false，不报错） */
  getAgent: (code: string) => apiGet<PromoAgentInfo>(`/promo/agent/${code}`),
  /** 公开：记录落地页访问（漏斗埋点，best-effort） */
  track: (payload: {
    promo_code: string;
    source?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  }) => apiPost<{ ok: boolean }>('/promo/track', payload),
};

export const agentBindingApi = {
  /** 路径B：商家自助填码绑定（首绑优先） */
  bind: (promo_code: string) =>
    apiPost<{ agent_name: string; promo_code: string }>('/agent/bind', { promo_code }),
  /** 查询我的推荐代理 */
  myBinding: () => apiGet<MyBinding>('/agent/my-binding'),
  /** 待我确认的代理认领请求（路径D） */
  myClaims: () => apiGet<{ items: ClaimItem[]; total: number }>('/agent/my-claims'),
  /** 确认认领 → 绑定 */
  confirmClaim: (id: string) =>
    apiPost<{ agent_name: string }>(`/agent/claims/${id}/confirm`),
  /** 拒绝认领 */
  rejectClaim: (id: string) =>
    apiPost<{ status: string }>(`/agent/claims/${id}/reject`),
};

// ---------------------------------------------------------------------------
// Public agent recruitment API (成为代理 — 公开申请，无需登录)
// ---------------------------------------------------------------------------

export interface AgentTierPublic {
  level: string;
  level_name: string;
  deposit_amount: number;
  commission_rate_first: number;
  commission_rate_renew: number;
  commission_rate_boost: number;
  protection_days: number;
  customer_limit: number;
  min_withdraw: number;
  requirements: string;
}

export interface AgentApplyPayload {
  target_level: string;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  company_name?: string;
  license_no?: string;
  region_code?: string;
  region_name?: string;
  team_size?: number;
  experience?: string;
  /** 招商页 /recruit?ref= 携带的推荐代理推广码（审批开户时挂 parent_agent_id） */
  ref_code?: string;
}

export const publicAgentApi = {
  /** 公开：三档代理等级（保证金/佣金/准入条件/保护期），供申请页选择目标等级 */
  getTiers: () => apiGet<{ items: AgentTierPublic[] }>('/agents/tiers'),
  /** 公开：提交成为代理申请（待管理员审核，通过后短信/电话通知） */
  apply: (req: AgentApplyPayload) =>
    apiPost<{ id: string; status: string }>('/agents/apply', req),
};
